import { useState } from 'react'
import { EntityAutocomplete } from 'components/ui/EntityAutocomplete'

interface User {
  id?: string
  user_uuid?: string
  email?: string
  real_name?: string
  firstName?: string
  first_name?: string
  lastName?: string
  last_name?: string
  photoUrl?: string
}

const UsersAutocomplete = ({
  users,
  userId,
  label,
  onChange,
  onBlur,
  multiple = true,
  defaultOwner,
}: {
  users: User[]
  userId?: string
  label: string
  onChange: (value: User | User[] | null) => void
  onBlur?: () => void
  multiple?: boolean
  defaultOwner?: string
}) => {
  const [selectedUsers, setSelectedUsers] = useState<User | User[] | null>(() => {
    const currentUser = users?.find(u => u.id === defaultOwner)
    return multiple ? (currentUser ? [currentUser] : []) : currentUser || null
  })

  const getUserDisplayName = (user: User): string => {
    if (user.real_name) return user.real_name
    const firstName = user.firstName || user.first_name || ''
    const lastName = user.lastName || user.last_name || ''
    return `${firstName} ${lastName}`.trim()
  }

  const getUserLabelWithEmail = (user: User): string => {
    const name = getUserDisplayName(user)
    return user.email ? `${name} (${user.email})` : name
  }

  const handleChange = (newValue: User | User[] | null) => {
    setSelectedUsers(newValue)
    onChange(newValue)
  }

  // Custom filter that searches by name parts
  const filterFn = (options: User[], inputValue: string): User[] => {
    if (!inputValue) return options
    const lowerInput = inputValue.toLowerCase()
    return options.filter(user => {
      const firstName = user.firstName || user.first_name || ''
      const lastName = user.lastName || user.last_name || ''
      const fullName = `${firstName} ${lastName}`.toLowerCase()
      return fullName.includes(lowerInput)
    })
  }

  return (
    <EntityAutocomplete<User>
      id="users-autocomplete"
      options={users || []}
      value={selectedUsers}
      onChange={handleChange}
      getOptionLabel={getUserLabelWithEmail}
      getOptionKey={user => user.id}
      label={label}
      multiple={multiple}
      filterFn={filterFn}
      onBlur={onBlur}
      secondaryText={{
        getText: user => user.email || '',
        fontSize: 12,
      }}
    />
  )
}

export default UsersAutocomplete
